import React, { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    EditIcon,
    H5Emphasized,
    InfoRow,
    RowList,
    Sheet,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../../../common/hooks/useSendEmailToSupport';
import { isEmpty } from '../../../../common/misc';
import { type User } from '../../../../user/types/User';
import { CardForm, type CardMailingAddress } from '../../../types/Card';

interface OrderCardDetailsProps {
    reviewMode: boolean;
    user: User;
    embossName: string;
    cardTypes: CardForm[];
    onPress: () => void;
    onPressEdit?: () => void;
}

const formatAddress = ({
    line1,
    line2,
    city,
    state,
    postalCode,
}: CardMailingAddress) => {
    return `${line1}\n${line2 ?? ''}${!isEmpty(line2) ? '\n' : ''}${city}, ${state} ${postalCode}`;
};

export const OrderCardDetails = ({
    reviewMode,
    user,
    embossName,
    cardTypes,
    onPress,
}: OrderCardDetailsProps) => {
    const { userAddress } = user;

    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
    const sendEmailToSupport = useSendEmailToSupport();

    const isOrderingPhysicalCard = cardTypes.includes(CardForm.PHYSICAL);

    return (
        <YStack flex={1} justifyContent="space-between" paddingHorizontal={16}>
            <YStack>
                <H5Emphasized>
                    {reviewMode ? 'Confirm your details' : 'Card details'}
                </H5Emphasized>
                <BodyMediumRegular
                    color="$gray1000"
                    marginTop={12}
                    marginBottom={24}
                >
                    {isOrderingPhysicalCard
                        ? "These are the details that will show on your cards, and the address they'll be sent to."
                        : 'These are the details that will show on your card.'}
                </BodyMediumRegular>

                <RowList>
                    <InfoRow title="Name on card" value={embossName} />
                    <InfoRow
                        title="Card type"
                        value={cardTypes
                            .map(
                                (cardType) =>
                                    cardType.charAt(0).toUpperCase() +
                                    cardType.substring(1).toLowerCase(),
                            )
                            .join(' and ')}
                    />
                    {isOrderingPhysicalCard && (
                        <InfoRow
                            title="Mailing address"
                            value={formatAddress({
                                ...(userAddress as CardMailingAddress),
                            })}
                        />
                    )}
                </RowList>
            </YStack>

            <YStack>
                {!reviewMode && (
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.LARGE}
                        text="Edit my information"
                        icon={EditIcon}
                        onPress={() => {
                            setIsEditSheetOpen(true);
                        }}
                    />
                )}
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={onPress}
                    text={reviewMode ? 'Confirm and complete' : 'Next'}
                />
            </YStack>

            <EditContactInfoSheet
                isOpen={isEditSheetOpen}
                onClose={() => {
                    setIsEditSheetOpen(false);
                }}
                onPressContactFruitful={() => {
                    setIsEditSheetOpen(false);
                    void sendEmailToSupport({
                        subject: 'Physical card request',
                    });
                }}
            />
        </YStack>
    );
};

interface EditContactInfoSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onPressContactFruitful: () => void;
}
const EditContactInfoSheet = ({
    isOpen,
    onClose,
    onPressContactFruitful,
}: EditContactInfoSheetProps) => {
    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton>
            <YStack $sm={{ minWidth: 400 }}>
                <BodyLargeEmphasized marginBottom={20}>
                    To edit this information, you&apos;ll need to contact
                    Fruitful Hospitality
                </BodyLargeEmphasized>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Contact Fruitful Hospitality"
                    onPress={onPressContactFruitful}
                />
            </YStack>
        </Sheet>
    );
};
